<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><font-awesome-icon icon="robot"/> AI Trợ lý — Lịch sử câu hỏi</h1>
        <p>Câu hỏi nhân viên gửi cho AI Trợ lý. Click vào dòng để xem chi tiết.</p>
      </div>
    </div>

    <div class="row">
      <!-- Tabs trạng thái -->
      <div class="col-12">
        <b-tabs v-model="statusTabIndex" @input="onStatusTab">
          <b-tab title="Tất cả"></b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #28a745"></span> Thành công
            </template>
          </b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #dc3545"></span> Lỗi
            </template>
          </b-tab>
          <b-tab>
            <template #title>
              <span class="log-dot" style="background: #ffc107"></span> Không dùng tool
            </template>
          </b-tab>
        </b-tabs>
      </div>

      <!-- Filter -->
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <b-form-input class="mr-1 mb-1" v-model="filter.keyword" v-on:keydown.enter="load"
                          placeholder="Tìm câu hỏi, câu trả lời..." style="min-width: 280px"></b-form-input>
            <b-form-input type="date" class="mr-1 mb-1" v-model="filter.from"
                          style="width: 160px" title="Từ ngày"></b-form-input>
            <span class="mx-1 mb-1">→</span>
            <b-form-input type="date" class="mr-1 mb-1" v-model="filter.to"
                          style="width: 160px" title="Đến ngày"></b-form-input>
            <button @click="load" class="btn btn-primary mr-1 mb-1">
              <font-awesome-icon icon="search"/> Tìm kiếm
            </button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
            <button @click="exportCSV" class="btn btn-success ml-auto mb-1" :disabled="exporting">
              <font-awesome-icon icon="download"/> CSV
            </button>
          </div>
        </div>
      </div>

      <!-- Paging top -->
      <div class="col-12">
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="totalDocs">
            Tổng {{ totalDocs }} kết quả
          </div>
          <div class="c-paging__right">
            <b-pagination v-if="totalDocs > pageSize" class="pull-right" size="sm"
                          v-model="page" :total-rows="totalDocs" :per-page="pageSize"
                          @change="onPageChange"></b-pagination>
          </div>
        </div>
      </div>

      <!-- Card list -->
      <div class="col-12" v-if="items.length">
        <div class="log-list">
          <div v-for="item in items" :key="item._id"
               class="log-card" :class="'log-card--' + (item.status || 'unknown')"
               @click="showDetail(item)" role="button">
            <div class="log-card__time">
              <div class="log-time-main">{{ item.createTime | moment('DD/MM HH:mm') }}</div>
              <div class="log-meta">{{ item.duration ? (item.duration / 1000).toFixed(1) + 's' : '—' }}</div>
            </div>

            <div class="log-card__main">
              <div class="log-row-1">
                <span class="badge mr-2" :class="'badge-' + statusVariant(item.status)">
                  {{ statusLabel(item.status) }}
                </span>
                <span v-if="item.client" class="log-client mr-3">
                  <font-awesome-icon icon="building" class="text-muted mr-1"/>
                  {{ item.client.companyName || item.client.contactName || item.client.domain || '—' }}
                </span>
                <span v-if="item.user" class="log-user">
                  <font-awesome-icon icon="user" class="text-muted mr-1"/>
                  {{ item.user.name }} <span class="log-meta">({{ item.user.ext }})</span>
                </span>
              </div>
              <div class="log-row-2 log-question">
                <font-awesome-icon icon="question-circle" class="text-info mr-1"/>
                {{ truncate(item.question, 140) }}
              </div>
              <div v-if="item.toolsUsed && item.toolsUsed.length" class="log-row-3">
                <span class="log-meta mr-1">Tools:</span>
                <b-badge v-for="t in item.toolsUsed" :key="t" variant="info" class="mr-1">{{ t }}</b-badge>
              </div>
            </div>

            <div class="log-card__chev">
              <font-awesome-icon icon="chevron-right"/>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 text-center text-muted py-4" v-if="!items.length">
        <font-awesome-icon icon="inbox" size="3x" class="mb-2 d-block"/>
        Không có dữ liệu
      </div>

      <!-- Paging bottom -->
      <div class="col-12" v-if="totalDocs > pageSize">
        <div class="c-paging clearfix">
          <div class="c-paging__count">Tổng {{ totalDocs }} kết quả</div>
          <div class="c-paging__right">
            <b-pagination class="pull-right" size="sm"
                          v-model="page" :total-rows="totalDocs" :per-page="pageSize"
                          @change="onPageChange"></b-pagination>
          </div>
        </div>
      </div>
    </div>

    <!-- Detail modal -->
    <b-modal v-model="showModal" size="xl" hide-footer scrollable
             :modal-class="['log-modal']" body-class="log-modal-body">
      <template #modal-title>
        <span v-if="detail">
          <font-awesome-icon icon="robot"/> Chi tiết câu hỏi
          <span class="ml-2 small text-muted">{{ detail.createTime | moment('DD/MM/YYYY HH:mm:ss') }}</span>
        </span>
      </template>

      <div v-if="detail">
        <div class="log-meta-grid">
          <div class="log-meta-cell">
            <div class="log-meta-label">Trạng thái</div>
            <span class="badge" :class="'badge-' + statusVariant(detail.status)">{{ statusLabel(detail.status) }}</span>
          </div>
          <div class="log-meta-cell">
            <div class="log-meta-label">Thời gian xử lý</div>
            <div>{{ detail.duration ? (detail.duration / 1000).toFixed(1) + 's' : '—' }}</div>
          </div>
          <div class="log-meta-cell" v-if="detail.tokenUsage">
            <div class="log-meta-label">Token</div>
            <div>{{ detail.tokenUsage.totalTokens }}
              <span class="log-meta">(in={{ detail.tokenUsage.promptTokens }}, out={{ detail.tokenUsage.completionTokens }})</span>
            </div>
          </div>
          <div class="log-meta-cell">
            <div class="log-meta-label">Khách hàng</div>
            <div v-if="detail.client">
              {{ detail.client.companyName || detail.client.contactName || '—' }}
              <span class="log-meta" v-if="detail.client.domain">({{ detail.client.domain }})</span>
            </div>
            <div v-else>—</div>
          </div>
          <div class="log-meta-cell log-meta-cell--wide">
            <div class="log-meta-label">Nhân viên</div>
            <div v-if="detail.user">{{ detail.user.name }} <span class="log-meta">({{ detail.user.ext }})</span></div>
            <div v-else>—</div>
          </div>
        </div>

        <div v-if="detail.toolsUsed && detail.toolsUsed.length" class="log-section">
          <div class="log-section__label">Tools đã dùng</div>
          <b-badge v-for="t in detail.toolsUsed" :key="t" variant="info" class="mr-1">{{ t }}</b-badge>
        </div>

        <div v-if="detail.errorMessage" class="alert alert-danger mt-3">
          <strong>Lỗi:</strong> {{ detail.errorMessage }}
        </div>

        <div class="log-section">
          <div class="log-section__label log-section__label--big">
            <font-awesome-icon icon="question-circle" class="text-info mr-2"/> Câu hỏi
          </div>
          <div class="log-content-block">{{ detail.question }}</div>
        </div>

        <div class="log-section">
          <div class="log-section__label log-section__label--big">
            <font-awesome-icon icon="robot" class="text-success mr-2"/> Câu trả lời
          </div>
          <div class="log-content-block">{{ detail.answer || '—' }}</div>
        </div>
      </div>
    </b-modal>
  </main>
</template>

<script>
export default {
  data() {
    var now = new Date();
    var sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return {
      items: [],
      page: 1,
      pageSize: 20,
      totalDocs: 0,
      statusTabIndex: 0,
      filter: {
        status: '',
        keyword: '',
        from: sevenDaysAgo.toISOString().split('T')[0],
        to: now.toISOString().split('T')[0],
      },
      showModal: false,
      detail: null,
      exporting: false,
    };
  },
  methods: {
    load() {
      var params = { page: this.page, pageSize: this.pageSize };
      if (this.filter.status) params.status = this.filter.status;
      if (this.filter.keyword) params.keyword = this.filter.keyword;
      if (this.filter.from) params.from = new Date(this.filter.from).getTime();
      if (this.filter.to) params.to = new Date(this.filter.to).getTime() + 24 * 60 * 60 * 1000 - 1;

      this.$service.get('/ai-assistant/logs', { params: params }).then(res => {
        this.items = res.data.docs;
        this.totalDocs = res.data.totalDocs;
      });
    },
    onPageChange(page) {
      this.page = page;
      this.load();
    },
    onStatusTab(idx) {
      const map = ['', 'success', 'error', 'no_tool'];
      this.filter.status = map[idx] || '';
      this.page = 1;
      this.load();
    },
    showDetail(item) {
      this.detail = item;
      this.showModal = true;
    },
    reset() {
      var now = new Date();
      var sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      this.filter = {
        status: '',
        keyword: '',
        from: sevenDaysAgo.toISOString().split('T')[0],
        to: now.toISOString().split('T')[0],
      };
      this.statusTabIndex = 0;
      this.page = 1;
      this.load();
    },
    exportCSV() {
      this.exporting = true;
      var params = {};
      if (this.filter.status) params.status = this.filter.status;
      if (this.filter.keyword) params.keyword = this.filter.keyword;
      if (this.filter.from) params.from = new Date(this.filter.from).getTime();
      if (this.filter.to) params.to = new Date(this.filter.to).getTime() + 24 * 60 * 60 * 1000 - 1;

      this.$service.get('/ai-assistant/export', { params: params, responseType: 'blob' })
        .then(res => {
          var url = window.URL.createObjectURL(new Blob([res.data]));
          var a = document.createElement('a');
          a.href = url;
          a.download = 'ai-assistant-logs.csv';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        })
        .finally(() => { this.exporting = false; });
    },
    truncate(str, len) {
      if (!str) return '—';
      return str.length > len ? str.substring(0, len) + '...' : str;
    },
    statusVariant(status) {
      if (status === 'success') return 'success';
      if (status === 'error') return 'danger';
      if (status === 'no_tool') return 'warning';
      return 'secondary';
    },
    statusLabel(status) {
      if (status === 'success') return 'Thành công';
      if (status === 'error') return 'Lỗi';
      if (status === 'no_tool') return 'Không tool';
      return 'Chưa rõ';
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.log-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}

.log-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.log-card {
  display: flex;
  align-items: stretch;
  background: #fff;
  border: 1px solid #e8eaed;
  border-left: 4px solid #888;
  border-radius: 6px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.15s;
  gap: 16px;
}
.log-card:hover {
  border-color: #c9d1d9;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}
.log-card--success { border-left-color: #28a745; }
.log-card--error { border-left-color: #dc3545; background: #fef6f6; }
.log-card--no_tool { border-left-color: #ffc107; }

.log-card__time {
  min-width: 110px;
  flex-shrink: 0;
}
.log-time-main {
  font-size: 13px;
  color: #333;
  font-weight: 500;
}
.log-meta {
  font-size: 11px;
  color: #888;
}

.log-card__main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.log-row-1 { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 0; }
.log-row-2 { font-size: 13px; color: #444; }
.log-question {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.log-client, .log-user { font-size: 13px; color: #555; }

.log-card__chev {
  display: flex;
  align-items: center;
  color: #bbb;
  flex-shrink: 0;
}

/* Modal */
.log-meta-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
}
.log-meta-cell--wide { grid-column: span 3; }
.log-meta-label {
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}
.log-section {
  margin-top: 20px;
}
.log-section__label {
  font-size: 12px;
  color: #888;
  text-transform: uppercase;
  margin-bottom: 6px;
}
.log-section__label--big {
  font-size: 14px;
  color: #222;
  text-transform: none;
  font-weight: 600;
  padding-bottom: 6px;
  border-bottom: 1px solid #e8eaed;
}
.log-content-block {
  background: #f6f8fa;
  padding: 12px;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
  line-height: 1.6;
}
</style>
<style>
.log-modal .modal-xl { max-width: 1100px; }
</style>
